import { Box, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import { File, ThumbnailType, FileThumbnail } from "types";
import { ImageViewer } from "components/ImageViewer";
import { FileFallback } from "./FileFallback";
import { useEffect, useState } from "react";
import TooltipIconButton from "components/Tooltip/TooltipIconButton";
import { Clear } from "@mui/icons-material";
import { PanoramaViewer } from "components/PanoramaViewer";
import { FileViewSwitcher } from "./FileViewSwitcher";
import { ControlledBar, Controls as ControlledBarControls } from "./ControlledBar";
import { ViewInFullscreenButton } from "./ViewInFullscreenButton";
import { LazyVideoPlayer } from "components/VideoPlayer";
import { PdfView } from './PdfView';

export type FileViewProps = {
    file: File,
    thumbnails: FileThumbnail[];
    fileHref: string,
    fileView: ThumbnailType;
    allowZoomPanPinch?: boolean;
    fallbackMessage?: boolean;
    displayHeader?: boolean,
    hideFullScreenButton?: boolean
    cursor?: string;
    additionalControls?: ControlledBarControls;
    onClick?: () => void;
    onClose?: () => void;
};

export type FileViewType = "icon" | "image" | "image-panorama" | "pdf-viewer" | "video" | "video-panorama";

function getDefaultImageView(type: string, fileView: ThumbnailType): FileViewType {

    if (type.startsWith("image"))
        return mediaPreference.image;

    if (type.startsWith("video"))
        return mediaPreference.video;

    if (fileView === "tile")
        return "icon";

    if (type === "application/pdf")
        return "pdf-viewer"

    return "icon";
}

const mediaPreference: {
    image: "image" | "image-panorama";
    video: "video" | "video-panorama";
} = {
    image: "image",
    video: "video"
};

export function FileView(props: FileViewProps) {

    const { file, fileHref, cursor, fallbackMessage, fileView, displayHeader = true, onClose, additionalControls, onClick } = props;
    const [fileViewType, setFileViewType] = useState<FileViewType>(getDefaultImageView(file.type, fileView));

    const controls: ControlledBarControls = [
        ...(additionalControls ? additionalControls : []),
        { control: <Typography variant="body1" className="fileView-header-fileName">{file.title}</Typography>, position: "start" as const },
        { control: <FileViewSwitcher fileViewType={fileViewType} onChange={handleMediaPreferenceChange} />, position: "end" as const },
        { control: <ViewInFullscreenButton show={fileView === "preview" && !props.hideFullScreenButton} onClick={onClick} />, position: "end" as const },
        ...(onClose ? [{ control: <TooltipIconButton Icon={Clear} buttonId="exitFullscreen" onClick={onClose} className="fileView-header-closeButton" sx={{ padding: "4px" }} />, position: "end" as const }] : [])
    ];

    return (
        <Stack width="100%" height="100vh" spacing={1}>
            {displayHeader &&
                <>
                    <ControlledBar className="fileView-header" height="39px" controls={controls} />
                    <Divider sx={{ marginTop: "0 !important" }} />
                </>
            }
            <Box flexGrow={1} height="50%" id="fileView-render">
                {fileHref && renderFileView(fileViewType)}
            </Box>
        </Stack >
    );

    function onImageLoadError() {
        setFileViewType("icon");
    }

    function renderFileView(fileViewType: FileViewType): React.ReactNode {
        switch (fileViewType) {
            case "image":
                return <ImageViewer {...props} name={file.title} url={fileHref} fileView={fileView} cursor={cursor} onError={onImageLoadError} />
            case "image-panorama":
                return <PanoramaViewer url={fileHref} />
            case "pdf-viewer":
                return <PdfView fileHref={fileHref} title={file.title} active={fileViewType === 'pdf-viewer'} />
            case "video":
            case "video-panorama":
                return <LazyVideoPlayer src={fileHref} panorama={fileViewType.includes("panorama")} />
            default:
                return <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    <FileFallback name={file.title} message={fallbackMessage ? `No preview available for "${file.title}"` : undefined} />
                </Box>;
        }
    }

    function handleMediaPreferenceChange(next: FileViewType) {
        setFileViewType(next)
        if (next === "image" || next === "image-panorama") {
            mediaPreference.image = next;
        } else if (next === "video" || next === "video-panorama") {
            mediaPreference.video = next;
        }
    }
}